// Role helpers — extracted from functions/index.js (2026-08-12) so leaf
// consumers (AdminChrome and other components reachable from the
// workspace-shared components barrel) can import them WITHOUT pulling the
// whole shell functions barrel. The barrel re-exports everything here, so
// `el.call('roleOf', …)` and existing barrel imports are unchanged.
//
// Why this matters: shared context.js → shared components barrel →
// IdentityAdminComponents → AdminChrome ran DURING the shared package's own
// module init. When AdminChrome imported the shell functions barrel, every
// module-scope `_wsShared.functions` read in that barrel executed against a
// still-initializing shared barrel (default export undefined) and crashed
// the entire shell boot. Keeping AdminChrome on this leaf module keeps the
// shell functions barrel out of the shared init cycle.

const _rolesByName = (s) => {
  const list = s && s.root && s.root.roles && s.root.roles.available
  if (!list || !list.length) return {}
  const map = {}
  for (const r of list) map[r.name] = r
  return map
}

export const allRoles = (s) => (s && s.root && s.root.roles && s.root.roles.available) || []

export const roleOf = (s, userId) =>
  (s && s.root && s.root.roles && s.root.roles.byUser && s.root.roles.byUser[userId]) || null

// OrgMember._id for a given userId — the id `sdk.updateOrgMember(orgId,
// memberId, { role })` PATCHes (NOT the user id). Populated by
// loadPrivateData._loadRoles alongside `byUser`, both sourced from the same
// sdk.listOrgMembers(orgId) call.
export const memberIdOf = (s, userId) =>
  (s &&
    s.root &&
    s.root.roles &&
    s.root.roles.byUserMemberId &&
    s.root.roles.byUserMemberId[userId]) ||
  null

export const currentRole = (s) => roleOf(s, s && s.root && s.root.currentUserId)

export const roleMeta = (s, role) => _rolesByName(s)[role] || null

export const roleColor = (s, role) => roleMeta(s, role)?.color || 'gray'
// A person with no role is a plain member: both render sites default the role
// to 'member' BEFORE asking, and 'member' is a system role whose word comes from
// the bundle (roleLabelKey). This helper therefore never names a role of its
// own — it answers the label a workspace gave a role, or the capitalised id.
export const roleLabel = (s, role) =>
  roleMeta(s, role)?.label || (role ? role.replace(/^./, (c) => c.toUpperCase()) : '')
export const roleIsCore = (s, role) => !!roleMeta(s, role)?.is_core

// The bundle KEY of a SYSTEM role, or null for a tenant-defined one. The role
// chips read `Owner` / `Co-owner` / `Member` on a Georgian page (measured live
// on /people and /admin): `roleLabel` above has no element, so it cannot reach
// polyglot, and it answered the server's English label. The strings already
// ship in both languages — shared MemberProfile resolves the same keys. A
// render site resolves the key; a role a workspace invented has no key and
// keeps the label its admin wrote. Literal keys, never concatenated
// (docs/I18N-CONVENTION.md §1.3).
const ROLE_LABEL_KEYS = {
  owner: 'account.member.role.owner',
  'co-owner': 'account.member.role.coOwner',
  superadmin: 'account.member.role.superadmin',
  admin: 'account.member.role.admin',
  manager: 'account.member.role.manager',
  member: 'account.member.role.member',
  editor: 'account.member.role.editor',
  viewer: 'account.member.role.viewer',
  guest: 'account.member.role.guest'
}
export const roleLabelKey = (role) => {
  const key = ROLE_LABEL_KEYS[role]
  return typeof key === 'string' ? key : null
}

export const isCoreMember = (s) => {
  const role = currentRole(s)
  if (!role) return false
  return roleIsCore(s, role)
}

// Your own People row: by user id, or by email for a row with no id.
// `el.call('isOwnPersonRow', s)` reads the row state's own root.
export const isOwnPersonRow = (person, root) => {
  const r = root || (person && person.root)
  if (!person || !r) return false
  if (person.userId && r.currentUserId) return person.userId === r.currentUserId
  return !!person.email && person.email === r.currentUserEmail
}

// People › Role manager (components/PersonCard.js RoleManagerRow). A role
// change asks first (CRUD standard rule 4, D1): the title names the person
// and the new role, and nothing is written before "Change role". Your own
// row never sends one: the server refuses an own-role write (403), so the
// row's select is off with that reason and this guard says it too.
// `showRole(role)` sets the select: the stored role again on Cancel, on the
// refusal and on a failed write; the new role once it is stored.
export function confirmPersonRoleChange(person, role, showRole) {
  const root = (person && person.root) || this?.state?.root
  const s = { root }
  const prev = (root && person && roleOf(s, person.userId)) || 'member'
  const show = (value) => {
    if (typeof showRole === 'function') showRole(value)
  }
  const back = () => show(prev)
  if (!root || !person?.userId || !role || role === prev) {
    back()
    return
  }
  const t = (key) => this.call('polyglot', key)
  if (isOwnPersonRow(person, root)) {
    back()
    this.call('openNotification', {
      type: 'warning',
      title: t('members.role.changeFailed'),
      message: t('workspace.person.roleChange.self')
    })
    return
  }
  const name = person.name || person.email || t('members.roleUpdated.fallbackName')
  const labelKey = roleLabelKey(role)
  const label = labelKey ? t(labelKey) : roleLabel(s, role)
  const failed = (err) => {
    back()
    this.call('openNotification', {
      type: 'error',
      title: t('members.role.changeFailed'),
      message: (err && err.message) || ''
    })
  }
  this.call('confirmDialog', {
    title: t('workspace.person.roleChange.title')
      .replace('{name}', () => name)
      .replace('{role}', () => label),
    message: t('workspace.person.roleChange.message'),
    confirmText: t('workspace.person.roleChange.confirm'),
    cancelText: t('common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onCancel: back,
    onConfirm: async () => {
      // memberId = OrgMember._id (not the user id): what
      // sdk.updateOrgMember(orgId, memberId, { role }) PATCHes.
      const orgId = root.jwtClaims?.workspace_org_id
      const memberId = memberIdOf(s, person.userId)
      if (!orgId || !memberId || !this.sdk?.updateOrgMember) return failed(null)
      try {
        await this.sdk.updateOrgMember(orgId, memberId, { role })
      } catch (err) {
        return failed(err)
      }
      // Applied only AFTER a confirmed success: the row never shows a role
      // the PATCH did not persist.
      const prev = root.roles || { available: [], byUser: {}, byUserMemberId: {}, version: 0 }
      root.update({
        roles: {
          ...prev,
          byUser: { ...(prev.byUser || {}), [person.userId]: role },
          version: (prev.version || 0) + 1
        }
      })
      show(role)
      this.call('openNotification', {
        type: 'success',
        title: t('members.roleUpdated.title'),
        message: t('members.roleUpdated.message')
          .replace('{name}', () => name)
          .replace('{role}', () => label)
      })
      return true
    }
  })
}
